import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Plus, Search, ChevronRight, ArrowUpDown, ClipboardList, Clock, CheckCircle2, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { MetricCard, MetricLabel, MetricValue } from '../../components/ui/metric-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import AdminShell from '../../components/Survey/admin/AdminShell';
import { prettyDate, statusVariant } from '../../components/Survey/admin/surveyUiUtils';
import { Actions as SURVEY_ACTIONS } from '../../store/actions/surveyActions';

const EmptyState = () => (
  <Card className='border-dashed'>
    <CardContent className='py-16 flex flex-col items-center text-center'>
      <div className='w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-5 text-accent'>
        <ClipboardList size={22} strokeWidth={1.5} />
      </div>
      <h3 className='font-display text-xl mb-2'>No campaigns yet</h3>
      <p className='text-sm text-muted-foreground max-w-[420px] mb-6'>
        Create your first WFH &amp; Commuting survey campaign to collect employee data for Scope 3 Cat 7 reporting.
      </p>
      <Button size='lg' asChild>
        <Link to='/surveys/new'>
          <Plus size={16} strokeWidth={1.5} />
          Create campaign
        </Link>
      </Button>
    </CardContent>
  </Card>
);

const SurveyList = ({ campaigns, invitationTotals, loading, error, currentOrganisation, listCampaigns }) => {
  const history = useHistory();
  const [query, setQuery] = React.useState('');

  useEffect(() => {
    if (currentOrganisation) listCampaigns();
  }, [currentOrganisation, listCampaigns]);

  const filteredCampaigns = useMemo(() => {
    if (!query) return campaigns;
    const term = query.toLowerCase();
    return campaigns.filter(
      (campaign) =>
        String(campaign?.name || '').toLowerCase().includes(term) ||
        String(campaign?.status || '').toLowerCase().includes(term),
    );
  }, [campaigns, query]);

  const totals = useMemo(() => {
    const active = campaigns.filter((c) => String(c?.status).toLowerCase() === 'active').length;
    const closed = campaigns.filter((c) => String(c?.status).toLowerCase() === 'closed').length;
    const invitedFromApi = invitationTotals?.invitationsSent;
    const completedFromApi = invitationTotals?.invitationsCompleted;
    const invited =
      invitedFromApi != null && Number.isFinite(Number(invitedFromApi))
        ? Number(invitedFromApi)
        : campaigns.reduce((sum, c) => sum + Number(c?.invitationCount || c?.invitedCount || 0), 0);
    const submitted =
      completedFromApi != null && Number.isFinite(Number(completedFromApi))
        ? Number(completedFromApi)
        : campaigns.reduce((sum, c) => sum + Number(c?.submittedCount || c?.completedCount || 0), 0);
    const responseRate = invited > 0 ? Math.round((submitted / invited) * 100) : 0;
    return { active, closed, invited, submitted, responseRate };
  }, [campaigns, invitationTotals]);

  return (
    <AdminShell
      breadcrumbs={[{ label: 'Sustainability' }, { label: 'Surveys' }]}
      eyebrow='Surveys'
      title='WFH & Commuting Campaigns'
      description='Collect employee working-from-home and commuting data for your Scope 3 Category 7 footprint. Each campaign represents one reporting period.'
      actions={
        <Button asChild>
          <Link to='/surveys/new'>
            <Plus size={16} strokeWidth={1.5} />
            New campaign
          </Link>
        </Button>
      }>
      {!loading?.campaigns && campaigns.length === 0 && !error ? (
        <EmptyState />
      ) : (
        <>
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
            <MetricCard>
              <MetricLabel>Active campaigns</MetricLabel>
              <MetricValue>{totals.active}</MetricValue>
              <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
                <Clock size={12} strokeWidth={1.5} />
                Currently collecting responses
              </div>
            </MetricCard>
            <MetricCard accent='success'>
              <MetricLabel>Closed campaigns</MetricLabel>
              <MetricValue>{totals.closed}</MetricValue>
              <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
                <CheckCircle2 size={12} strokeWidth={1.5} />
                Results available
              </div>
            </MetricCard>
            <MetricCard accent='amber'>
              <MetricLabel>Responses collected</MetricLabel>
              <MetricValue>{totals.submitted.toLocaleString()}</MetricValue>
              <div className='text-xs text-muted-foreground flex items-center gap-1.5 mt-2'>
                <Users size={12} strokeWidth={1.5} />
                across {totals.invited.toLocaleString()} invited
              </div>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Average response rate</MetricLabel>
              <MetricValue unit='%'>{totals.responseRate}</MetricValue>
            </MetricCard>
          </div>

          <Card>
            <div className='flex items-center gap-2 px-4 py-3 border-b border-border'>
              <div className='relative flex-1 max-w-sm'>
                <Search size={14} strokeWidth={1.5} className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground' />
                <Input
                  className='pl-9 h-8 text-sm'
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder='Search campaigns...'
                />
              </div>
              <Button variant='ghost' size='sm'>
                <ArrowUpDown size={14} strokeWidth={1.5} />
                Sort
              </Button>
            </div>
            {loading?.campaigns ? <div className='px-6 py-4 text-sm text-muted-foreground'>Loading campaigns...</div> : null}
            {error ? <div className='px-6 py-4 text-sm text-destructive'>{error}</div> : null}
            <Table wrapperClassName='rounded-none border-0 shadow-none'>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reporting period</TableHead>
                  <TableHead>Deadline</TableHead>
                  <TableHead className='text-right'>Response rate</TableHead>
                  <TableHead className='text-right'>Completed</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCampaigns.map((campaign) => {
                  const invited = Number(campaign?.invitationsSent || 0);
                  const completed = Number(campaign?.invitationsCompleted || 0);
                  const rate = invited > 0 ? Math.round((completed / invited) * 100) : null;
                  const isDraft = String(campaign?.status).toLowerCase() === 'draft';
                  const status = String(campaign?.status || '').toLowerCase();
                  return (
                    <TableRow
                      key={campaign.id}
                      className='cursor-pointer'
                      onClick={() => history.push(`/surveys/${campaign.id}`)}>
                      <TableCell className='font-medium'>
                        <div>{campaign.name || campaign.id}</div>
                        <div className='text-xs text-muted-foreground mt-0.5'>
                          Created {prettyDate(campaign.createdAt)} by {campaign.createdByName || '—'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={statusVariant(campaign.status)}
                          dot={status === 'active' || status === 'closing'}>
                          {campaign.statusLabel || campaign.status || 'draft'}
                        </Badge>
                      </TableCell>
                      <TableCell className='text-sm'>
                        {(
                          campaign.reportingPeriodStart && campaign.reportingPeriodEnd
                            ? `${prettyDate(campaign.reportingPeriodStart)} to ${prettyDate(campaign.reportingPeriodEnd)}`
                            : '—'
                        )}
                      </TableCell>
                      <TableCell className='text-sm'>{prettyDate(campaign.deadlineDate || campaign.deadline)}</TableCell>
                      <TableCell className='text-right tabular-nums'>
                        {isDraft || rate === null ? (
                          <span className='text-muted-foreground'>—</span>
                        ) : (
                          <span className='font-mono text-sm'>{rate}%</span>
                        )}
                      </TableCell>
                      <TableCell className='text-right tabular-nums'>
                        {isDraft ? (
                          <span className='text-muted-foreground'>—</span>
                        ) : (
                          <span className='font-mono text-sm'>{completed} / {invited}</span>
                        )}
                      </TableCell>
                      <TableCell className='text-right'>
                        <ChevronRight size={16} strokeWidth={1.5} className='text-muted-foreground' />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </AdminShell>
  );
};

const mapStateToProps = (state) => ({
  campaigns: state.survey?.campaigns || [],
  invitationTotals: state.survey?.invitationTotals || null,
  loading: state.survey?.loading || {},
  error: state.survey?.error,
  currentOrganisation: state.currentOrganisation,
});

const mapDispatchToProps = {
  listCampaigns: SURVEY_ACTIONS.listCampaigns,
};

export default connect(mapStateToProps, mapDispatchToProps)(SurveyList);
